/** Normalization for the profile link fields.
 *
 * `other_links` / `socials` historically held TWO incompatible shapes in one
 * `text[]` column: JSON strings written by customer-app
 * (`JSON.stringify({url, name})`), and bare URL strings written by support-app's
 * CSV import. The API now also serves the normalized `links` / `social_links`
 * object arrays, but the legacy fields remain until every consumer has moved.
 *
 * This is the TypeScript mirror of database-orm's `links.py`. The two must agree
 * — a link written by one and read by the other has to mean the same thing —
 * and `__tests__/profileLinks.test.ts` mirrors `tests/test_links.py`
 * case-for-case so a divergence fails somewhere.
 *
 * See plans/2026-08-07-profile-links-normalization-design.md.
 */

export type ProfileLink = { url: string; name?: string };

function clean(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asLink(entry: unknown): ProfileLink | null {
  let candidate = entry;

  if (typeof candidate === "string") {
    const text = candidate.trim();
    if (!text) return null;
    try {
      candidate = JSON.parse(text);
    } catch {
      // Not JSON, so the whole string is the URL (the CSV import shape).
      return { url: text };
    }
  }

  if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) {
    return null;
  }

  const url = clean((candidate as Record<string, unknown>).url);
  if (!url) return null;

  // A blank name is OMITTED rather than kept as "": otherwise {url} and
  // {url, name: ""} become two encodings of the same link and round-trip
  // comparisons stop being stable.
  const name = clean((candidate as Record<string, unknown>).name);
  return name ? { url, name } : { url };
}

/** Either legacy shape (or already-normalized objects) -> link objects.
 *  Order is preserved; entries without a usable URL are dropped. */
export function parseLinks(raw: unknown): ProfileLink[] {
  if (!Array.isArray(raw)) return [];
  const links: ProfileLink[] = [];
  for (const entry of raw) {
    const link = asLink(entry);
    if (link !== null) links.push(link);
  }
  return links;
}

/** Inverse of `parseLinks`, for the legacy field.
 *
 * `JSON.stringify` is already compact, which is what database-orm's
 * `to_legacy_strings` deliberately matches with `separators=(",", ":")`. Do not
 * pretty-print here: different bytes for an unchanged link make `diff()` report
 * a phantom change on every save. */
export function toLegacyStrings(links: unknown): string[] {
  return parseLinks(links).map((link) => JSON.stringify(link));
}

/** The rows an EDITOR should show, given a draft's normalized and legacy fields.
 *
 * Deliberately NOT `parseLinks`. An editor's rows are work-in-progress: a
 * freshly added row has an empty URL, and a half-typed one is not yet valid.
 * `parseLinks` drops both, so re-deriving editor rows through it on every render
 * makes "+ Add link" appear to do nothing — the row is appended, then filtered
 * away before it can be painted, with nothing thrown to explain it.
 *
 * So: an already-normalized array is returned AS-IS (blank rows and all), and
 * only the legacy string shape is parsed. Junk is discarded later, at save —
 * `diff()` runs `parseLinks` before sending, and `toLegacyStrings` drops
 * url-less rows from the legacy field.
 */
export function editableLinks(newValue: unknown, legacyValue: unknown): ProfileLink[] {
  if (Array.isArray(newValue)) return newValue as ProfileLink[];
  return parseLinks(legacyValue);
}
